import { useState, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PowerSchedule } from '@/features/plants/schemas/schedule-plant.schema.ts';

interface RenameScheduleModalProps {
  children: ReactNode;
  schedule: PowerSchedule;
  onRenameSchedule: (scheduleId: string, newName: string) => void;
}

export function RenameScheduleModal({
  children,
  schedule,
  onRenameSchedule,
}: RenameScheduleModalProps) {
  const [open, setOpen] = useState(false);
  const [newScheduleName, setNewScheduleName] = useState('');

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (newOpen) {
      setNewScheduleName(schedule.scheduleName);
    } else {
      setNewScheduleName('');
    }
  };

  const handleRenameSchedule = () => {
    if (newScheduleName.trim()) {
      onRenameSchedule(schedule.scheduleId, newScheduleName);
      setOpen(false);
      setNewScheduleName('');
    }
  };

  const handleCancel = () => {
    setOpen(false);
    setNewScheduleName('');
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Rename Schedule</DialogTitle>
          <DialogDescription>Enter a new name for this schedule.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="rename-name" className="text-right">
              Name
            </Label>
            <Input
              id="rename-name"
              value={newScheduleName}
              onChange={(e) => setNewScheduleName(e.target.value)}
              placeholder="Enter new name"
              className="col-span-3"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleRenameSchedule();
                }
              }}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleRenameSchedule} disabled={!newScheduleName.trim()}>
            Rename
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
